<template>
  <v-row class="background">
    <v-col cols="9">
      <custom-chart-renderer :title="title" v-if="hasData">
        <template v-slot:refresh>
          <v-btn icon color="green" @click="resetChart">
            <v-icon>mdi-cached</v-icon>
          </v-btn>
        </template>
        <scatter-chart
          :chartData="chartDataMonthSig"
          :options="optionsSig"
          @unzoomed="resetZ = false"
          :reset="resetZ"
          :autoRerender="false"
          :displayed="displayed"
        />
      </custom-chart-renderer>
      <span class="d-flex justify-center font-italic" v-else>
        {{ generateMessage('properties_360.readings.no_data') }}
      </span>
    </v-col>
    <v-col cols="3">
      <v-row>
        <v-col v-if="market === 'MC' || market === 'MCI'">
          <v-switch
            class="mt-0"
            v-model="consoProd"
            :label="
              consoProd
                ? generateMessage('properties_360.readings.combustible')
                : generateMessage('properties_360.readings.vente')
            "
            hide-details
          />
        </v-col>
        <v-col>
          <v-text-field
            :label="generateMessage('properties_360.readings.text')"
            v-model="tExt"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-simple-table
          v-if="formulas && formulas[0]"
          class="elevation-2 secondarybackground"
        >
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ generateMessage('properties_360.readings.annee') }}
                </th>
                <th class="text-left">
                  {{ generateMessage('properties_360.readings.r2') }}
                </th>
                <th class="text-left">
                  {{ generateMessage('properties_360.readings.pmoyen') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in formulas" :key="item.annee">
                <td>{{ item.annee }}</td>
                <td>{{ item.r2 }}</td>
                <td>{{ ((item.a * tExt + item.b) * 1000).toFixed() }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <v-simple-table
          v-if="formulas && formulas[0]"
          class="elevation-2 mt-2 secondarybackground"
        >
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  <v-select
                    :items="boilerListOrigins"
                    hide-details
                    single-line
                    v-model="boilerListOrigin"
                    class="text-caption pa-0"
                  >
                    <template v-slot:item="{ item }">
                      <span v-if="!item.empty">
                        <b>{{ item.text }}</b>
                      </span>
                      <span v-else>{{ item.text }}</span>
                    </template>
                  </v-select>
                </th>
                <th class="text-left">
                  {{ generateMessage('properties_360.readings.total') }} :
                  {{
                    totalPower - Math.trunc(totalPower) !== 0
                      ? totalPower.toFixed(1)
                      : totalPower
                  }}
                </th>
                <th class="text-left">
                  {{ generateMessage('properties_360.readings.unit') }}
                </th>
                <th class="text-center">
                  <v-btn
                    icon
                    x-small
                    :color="showColor('success')"
                    @click="addBoiler()"
                  >
                    <v-icon>
                      {{ generateMessage('properties_360.readings.icons.add') }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :color="showColor('error')"
                    @click="removeAllBoilers()"
                  >
                    <v-icon>
                      {{
                        generateMessage('properties_360.readings.icons.remove')
                      }}
                    </v-icon>
                  </v-btn>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in power"
                :key="index"
                draggable="true"
                :style="{ cursor: 'grab' }"
                @drop="drop_handler($event, index)"
                @dragover="dragover_handler($event)"
                @dragstart="dragstart_handler($event, index)"
              >
                <td>
                  <v-tooltip top v-if="item.comment">
                    <template v-slot:activator="{ on }">
                      <div
                        v-on="on"
                        :style="{
                          border: `dashed ${powerColor(index, item.disabled)}`,
                        }"
                      >
                        {{ item.name }}
                      </div>
                    </template>
                    <span>{{ item.comment }}</span>
                  </v-tooltip>
                  <div
                    v-else
                    :style="{
                      border: `dashed ${powerColor(index, item.disabled)}`,
                    }"
                  >
                    {{ item.name }}
                  </div>
                </td>
                <td>
                  <v-text-field
                    label="Puissance"
                    :id="`puissance${index}`"
                    v-model="power[index].value"
                    @change="cumulatePower()"
                    @keydown.enter="selectNext(index)"
                    @focus="selectText(index)"
                    type="number"
                  ></v-text-field>
                </td>
                <td>
                  {{ item.unit || 'kW' }}
                </td>
                <td>
                  <v-btn
                    icon
                    x-small
                    :color="showColor('grey')"
                    @click="enableDisableBoiler(index)"
                  >
                    <v-icon v-if="item.disabled">
                      {{
                        generateMessage(
                          'properties_360.readings.icons.disabled'
                        )
                      }}
                    </v-icon>
                    <v-icon v-else>
                      {{
                        generateMessage('properties_360.readings.icons.enabled')
                      }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :color="showColor('error')"
                    @click="removeBoiler(index)"
                  >
                    <v-icon>
                      {{
                        generateMessage('properties_360.readings.icons.remove')
                      }}
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import CustomChartRenderer from '@/components/graphs/CustomChartRenderer';
import ScatterChart from '@/components/graphs/ScatterChart';
import regression from 'regression';
import { generateMessage } from '@/utils/i18n';
import { showColor } from './ReadingsUtils';

const defaultPower = {
  value: 0,
  cumul: 0,
  name: 'Chaudière 1',
  unit: '',
  comment: '',
  disabled: false,
};
const colors = ['main', 'error', 'success', 'info', 'warning', 'accent'];

export default {
  name: 'ReadingSignature',
  components: {
    CustomChartRenderer,
    ScatterChart,
  },
  props: {
    baseTemperature: { type: Number, required: false },
    dataIn: { type: Array, required: false },
    market: { type: String, required: false },
    properties_360: { type: Object, required: false },
    displayed: { type: Boolean, required: false },
  },
  data() {
    return {
      formulas: [],
      consoProd: true,
      boilerListOrigin: 'sai',
      boilerListOriginText: 'Puissance C&C',
      boilerListOrigins: [
        { value: 'cc', text: 'Puissance C&C', empty: true },
        { value: 'gde', text: 'Inventaire GDE', empty: true },
        { value: 'p3', text: 'Inventaire P3', empty: true },
        { value: 'sai', text: 'Saisie', empty: true },
      ],
      tExt: -7,
      power: [{ ...defaultPower }],
      powerC: [],
      powerI: [],
      power3: [],
      technicalInventories: [],
      resetZ: false,
    };
  },
  methods: {
    resetChart() {
      this.resetZ = true;
    },
    showColor,
    generateMessage,
    dragstart_handler(event, index) {
      // On ajoute l'identifiant de l'élément cible à l'objet de transfert
      const myEvent = event;
      myEvent.dataTransfer.setData('boilerIndex', index);
      myEvent.dataTransfer.dropEffect = 'move';
    },
    dragover_handler(event) {
      const myEvent = event;
      myEvent.preventDefault();
      myEvent.dataTransfer.dropEffect = 'move';
    },
    drop_handler(event, index) {
      const myEvent = event;
      myEvent.preventDefault();
      // On récupère l'identifiant de la cible et on ajoute l'élément déplacé au DOM de la cible
      const boilerToMove = myEvent.dataTransfer.getData('boilerIndex');
      this.power.splice(index, 0, this.power.splice(boilerToMove, 1)[0]);
      this.cumulatePower();
    },
    initGDEPower(val) {
      this.technicalInventories = val
        .map((item) => ({
          technical_inventory_name: item.technical_inventory_name,
          level: item.level,
          comment: item.comment,
          value: Number(
            (
              item.power_nominal_kw ||
              item.power_heat_kw ||
              item.power_kw ||
              '0-kW'
            )
              .split('-')[0]
              .replace(',', '.')
          ),
          unit: (
            item.power_nominal_kw ||
            item.power_heat_kw ||
            item.power_kw ||
            '0-kW'
          ).split('-')[1],
          disabled: false,
        }))
        .filter((item) => item.level === 'M' && item.value > 0);

      if (this.technicalInventories.length) {
        this.powerI = this.technicalInventories.map((element) => ({
          value: Number(element.value),
          cumul: 0,
          name: element.technical_inventory_name,
          unit: element.unit,
          comment: element.comment,
          disabled: false,
        }));
        this.boilerListOrigins.find((e) => e.value === 'gde').empty = false;
      } else {
        this.powerI = [
          {
            value: 0,
            cumul: 0,
            name: 'Chaudière 1',
            unit: '',
            comment: '',
            disabled: false,
          },
        ];
      }
    },
    refreshBoilerList() {
      this.boilerListOrigin = 'cc';
    },
    initCCPower(val) {
      if (val && val.maintenance && val.maintenance.power_hot) {
        this.powerC = [
          {
            value: val.maintenance.power_hot,
            cumul: 0,
            name: 'Chaudière 1',
            unit: '',
            comment: '',
            disabled: false,
          },
        ];
        // this.powerC[0].value = val.maintenance.power_hot;
        if (val.maintenance.power_hot > 1) {
          this.boilerListOrigins.find((e) => e.value === 'cc').empty = false;
        }
      } else {
        this.powerC = [
          {
            value: 0,
            cumul: 0,
            name: 'Chaudière 1',
            unit: '',
            comment: '',
            disabled: false,
          },
        ];
      }
    },
    initP3Power(val) {
      val
        .filter(
          (elem) =>
            elem.equipment_type &&
            elem.equipment_type.toLowerCase().includes('chaud')
        )
        .forEach((element) => {
          this.power3.push({
            value: Number(element.sizing_value),
            cumul: 0,
            name: element.equipment_type,
            unit: element.sizing_unit,
            comment: element.brand_name,
            disabled: false,
          });
        });
      if (this.power3.length === 0) {
        this.power3 = [
          {
            value: 0,
            cumul: 0,
            name: 'Chaudière 1',
            unit: '',
            comment: '',
            disabled: false,
          },
        ];
      } else {
        this.boilerListOrigins.find((e) => e.value === 'p3').empty = false;
      }
    },
    selectNext(index) {
      if (index < this.power.length - 1) {
        document.getElementById(`puissance${index + 1}`).focus();
      }
    },
    selectText(index) {
      document.getElementById(`puissance${index}`).select();
    },
    removeAllBoilers() {
      this.boilerListOriginText = 'Saisie';
      this.boilerListOrigin = 'sai';
      this.power = [{ ...defaultPower }];
    },
    enableDisableBoiler(index) {
      this.power[index].disabled = !this.power[index].disabled;
      this.cumulatePower();
    },
    removeBoiler(index) {
      if (index < this.power.length) {
        this.power.splice(index, 1);
        if (this.power.length === 0) {
          this.power = [{ ...defaultPower }];
        }
      }
      this.cumulatePower();
    },
    addBoiler() {
      this.power.push({
        value: 0,
        cumul: this.power[this.power.length - 1].cumul,
        name: `Chaudière ${this.power.length + 1}`,
        comment: '',
        unit: 'kW',
        disabled: false,
      });
      this.cumulatePower();
    },
    cumulatePower() {
      let powerCumul = 0;
      this.power.forEach((elem, index) => {
        powerCumul += elem.disabled ? 0 : Number(elem.value);
        this.power[index].cumul = powerCumul;
        this.power[index].powerBackColor = this.powerColor(
          index,
          elem.disabled
        );
      });
    },
    powerColor(index, disable, transparence) {
      return this.showColor(disable ? 'grey' : colors[index % 6], transparence);
    },
    changeTooltips(items) {
      const dates = this.regressions
        .filter(
          (elem) => elem.x === items[0].xLabel && elem.y === items[0].yLabel
        )
        .map((elem) => new Date(elem.dt_evt).toLocaleDateString())
        .join(', ');
      return dates;
    },
    refreshTable(val) {
      const results = val
        .map((el) => {
          if (el.nbjour && el.nbjour !== 0) {
            if (this.market === 'F' || !this.consoProd) {
              return {
                x: this.baseTemperature - el.dj_reel / el.nbjour,
                y: +((el.nc_dj * el.dj_reel) / (24 * el.nbjour)),
                dt_evt: el.dt_evt,
                an: el.an,
              };
            }
            if (this.consoProd && this.market !== 'F') {
              return {
                x: this.baseTemperature - el.dj_reel / el.nbjour,
                y: +((el.conso_eltr_mwhpci * el.dj_reel) / (24 * el.nbjour)),
                dt_evt: el.dt_evt,
                an: el.an,
              };
            }
          }
          return {};
        })
        .filter((elem) => elem.x !== 0 && elem.y !== 0);

      this.regressions = results;

      // Préparation des données
      const resultats = this.years.map((year) =>
        results.filter((item) => item.an === year)
      );
      const regressionFormula = [];
      // Construction des courbes
      this.formulas = [];
      for (let i = 0; i < this.years.length; i += 1) {
        regressionFormula[i] = regression.linear(
          resultats[i].map((el) => [el.x, el.y]),
          {
            order: 2,
            precision: 4,
          }
        );

        this.formulas.push({
          annee: this.years[i],
          r2: regressionFormula[i].r2,
          a: regressionFormula[i].equation[0],
          b: regressionFormula[i].equation[1],
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      technical_inventories: 'properties_360/technical_inventories/items',
      warrenty_inventory: 'properties_360/warrenty_inventory/items',
    }),
    gridColor() {
      return this.$vuetify.theme.isDark ? '#3E414A' : '#DCDCDC';
    },
    optionsSig() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          point: {
            borderWidth: 2,
          },
        },
        legend: {
          position: 'bottom',
          labels: {
            fontColor: this.showColor('main'),
            filter(legendItem) {
              return legendItem.text !== 'TNC';
            },
          },
        },
        plugins: {
          zoom: {
            enabled: false,
            pad: { enabled: true, mode: 'x' },
            zoom: { enabled: true, mode: 'x' },
            pan: { enabled: true, mode: 'x' },
          },
        },
        scales: {
          xAxes: [
            {
              type: 'linear',
              position: 'bottom',
              ticks: { fontColor: this.showColor('main') },
              scaleLabel: {
                display: true,
                labelString: 'Base Temp - moyenne(DJ / jour) (°C)',
                fontColor: this.showColor('main'),
              },
              gridLines: { color: this.gridColor },
            },
          ],
          yAxes: [
            {
              ticks: { fontColor: this.showColor('main') },
              scaleLabel: {
                display: true,
                labelString: 'Puissance moyenne appelée kW en chauffe',
                fontColor: this.showColor('main'),
              },
              gridLines: { color: this.gridColor },
            },
          ],
        },
        tooltips: {
          position: 'nearest',
          yAlign: 'top',
          callbacks: {
            title: this.changeTooltips,
            label: (item) =>
              `${item.xLabel.toFixed(2)} - ${item.yLabel.toFixed(3)}`,
          },
        },
      };
    },
    title() {
      return this.generateMessage('properties_360.readings.sig_title');
    },
    totalPower() {
      return this.power.reduce((acc, boiler) => acc + Number(boiler.value), 0);
    },
    years() {
      const thisYear = new Date().getFullYear();
      return this.dataIn.length && this.dataIn[0].an
        ? [
            this.dataIn[this.dataIn.length - 1].an - 2,
            this.dataIn[this.dataIn.length - 1].an - 1,
            this.dataIn[this.dataIn.length - 1].an,
          ].filter((year) => year <= thisYear)
        : [];
    },
    hasData() {
      return this.chartDataMonthSig.datasets.some(
        (data) => this.years.includes(Number(data.label)) && data.data.length
      );
    },
    chartDataMonthSig() {
      // Transform data for visualization
      if (
        this.dataIn[0] &&
        (this.dataIn.some((el) => el.conso_tot) ||
          this.dataIn.some((el) => el.prod_tot))
      ) {
        const items = this.dataIn;
        const results = items
          .map((el) => {
            if (el.nbjour) {
              if (this.market === 'F' || !this.consoProd) {
                return {
                  x: this.baseTemperature - el.dj_reel / el.nbjour,
                  y: +((el.nc_dj * el.dj_reel * 1000) / (24 * el.nbjour)),
                  dt_evt: el.dt_evt,
                  an: el.an,
                };
              }
              if (this.consoProd && this.market !== 'F') {
                return {
                  x: this.baseTemperature - el.dj_reel / el.nbjour,
                  y: +(
                    (el.conso_eltr_mwhpci * el.dj_reel * 1000) /
                    (24 * el.nbjour)
                  ),
                  dt_evt: el.dt_evt,
                  an: el.an,
                };
              }
            }
            return {};
          })
          .filter((elem) => elem.x !== 0 && elem.y !== 0);

        // Préparation des données
        const resultats = this.years.map((year) =>
          results.filter((item) => item.an === year)
        );
        const regressionFormula = [];
        const regressionLine = [];
        const datasets = [];

        // Construction des courbes
        for (let i = 0; i < this.years.length; i += 1) {
          regressionFormula[i] = regression.linear(
            resultats[i].map((el) => [el.x, el.y]),
            {
              order: 2,
              precision: 4,
            }
          );
          regressionLine[i] = resultats[i]
            .map((el) => {
              return {
                x: el.x,
                y:
                  regressionFormula[i].equation[0] * el.x +
                  regressionFormula[i].equation[1],
              };
            })
            .sort((a, b) => a.x - b.x);
          datasets.push({
            label: `${this.years[i]}`,
            data: resultats[i].map((el) => ({
              x: el.x,
              y: el.y,
              dt_evt: el.dt_evt,
              an: el.an,
            })),
            borderColor: `${this.showColor(colors[i])}`,
            backgroundColor: 'transparent',
          });
          datasets.push({
            type: 'line',
            label: `${this.years[i]} : ${regressionFormula[i].string.substring(
              4
            )}`,
            data: regressionLine[i],
            _short: true,
            borderColor: `${this.showColor(colors[i])}`,
            backgroundColor: 'transparent',
            pointRadius: 0,
          });
          datasets.push({
            type: 'line',
            label: `TNC`,
            data: [
              {
                x:
                  +regressionFormula[i].equation[0] === 0
                    ? 0
                    : (
                        -regressionFormula[i].equation[1] /
                        regressionFormula[i].equation[0]
                      ).toFixed(3),
                y: 0,
              },
            ],
            borderColor: `${this.showColor(colors[i])}`,
            pointStyle: 'cross',
            pointRadius: 8,
          });
        }

        this.power.forEach((el, index) => {
          if (!el.disabled) {
            const element = Number(el.cumul);
            const powerBackColor = this.powerColor(index, false);
            datasets.push({
              type: 'line',
              label: el.name,
              data: [
                { x: 0, y: element },
                { x: 18, y: element },
              ],
              _short: true,
              borderColor: `${powerBackColor}`,
              backgroundColor: 'transparent',
              pointRadius: 0,
              borderDash: [10, 5],
            });
          }
        });
        return {
          datasets,
        };
      }
      return {
        datasets: [],
      };
    },
  },
  watch: {
    consoProd() {
      this.refreshTable(this.dataIn);
    },
    boilerListOrigin(val) {
      this.power.splice(0, this.power.length);
      switch (val) {
        case 'p3':
          this.boilerListOriginText = 'Inventaire P3';
          this.power = [...this.power3];
          break;
        case 'gde':
          this.boilerListOriginText = 'Inventaire GDE';
          this.power = [...this.powerI];
          break;
        case 'cc':
          this.boilerListOriginText = 'Puissance C&C';
          this.power = [...this.powerC];
          break;
        default:
          this.boilerListOriginText = 'Saisie';
          this.power = [
            {
              value: 0,
              cumul: 0,
              name: 'Chaudière 1',
              unit: '',
              comment: '',
              disabled: false,
            },
          ];
      }
      this.cumulatePower();
    },
    dataIn: {
      handler(val) {
        if (val.length) {
          this.refreshTable(val);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initCCPower(this.properties_360);
    this.initGDEPower(this.technical_inventories);
    this.initP3Power(this.warrenty_inventory);
    this.refreshBoilerList();
  },
};
</script>
